import {API_URL, fetchWithToken, patchWithToken, postWithToken} from "./apiBase";

export interface BulkInvitePublicView {
    valid: boolean;
    reason?: string | null;          // not_found | revoked | expired
    project_name?: string | null;
    project_number?: string | null;
    developer_company?: string | null;
}

export interface ScopeChoice {
    id: string;
    number: number | null;
    title: string;
    description: string;
    selected: boolean;
}

export interface BulkInviteRow {
    _id: string;
    token: string;
    label: string | null;
    created_at: string;
    expires_at: string | null;
    revoked: boolean;
    num_claims: number;
}

export interface BulkInviteClaimRow {
    _id: string;
    user_email: string;
    company_name: string | null;
    claimed_at: string;
    scope_ids: string[];
}

/**
 * Public - no token. Called before the visitor has signed up.
 * We read response.ok directly rather than using fetchAnonymous, because that
 * helper throws `new Error(errorData)` on a non-ok body, which stringifies to
 * "[object Object]".
 */
export async function getBulkInvitePublic(token: string): Promise<BulkInvitePublicView> {
    const response = await fetch(`${API_URL}/bulk-invite/${token}`);
    if (!response.ok) {
        return {valid: false, reason: 'not_found'};
    }
    return response.json();
}

export function claimBulkInvite(token: string, accessToken: string) {
    return postWithToken(`/bulk-invite/${token}/claim`, JSON.stringify({}), accessToken);
}

export function getBulkInviteScopes(token: string, accessToken: string): Promise<ScopeChoice[]> {
    return fetchWithToken(`/bulk-invite/${token}/scopes`, accessToken);
}

export function selectBulkInviteScopes(token: string, scope_ids: string[], accessToken: string) {
    return postWithToken(`/bulk-invite/${token}/scopes`, JSON.stringify({scope_ids}), accessToken);
}

// --- developer side ---

export function createBulkInvite(project_id: string, label: string | null, accessToken: string) {
    return postWithToken(`/bulk-invite/${project_id}`, JSON.stringify({label}), accessToken);
}

export function listBulkInvites(project_id: string, accessToken: string): Promise<BulkInviteRow[]> {
    return fetchWithToken(`/bulk-invite/${project_id}/invites`, accessToken);
}

export function listBulkInviteClaims(project_id: string, token: string, accessToken: string): Promise<BulkInviteClaimRow[]> {
    return fetchWithToken(`/bulk-invite/${project_id}/invites/${token}/claims`, accessToken);
}

export function revokeBulkInvite(project_id: string, token: string, accessToken: string) {
    return patchWithToken(`/bulk-invite/${project_id}/invites/${token}/revoke`, JSON.stringify({}), accessToken);
}
